import { GameConfig } from "../config/game-config";
import { Assets } from "./resources";
import { IVector2 } from "../types/game-config-type";
import { Vector2 } from "../math/vector2";
import { Orientation } from "../math/orientation";
import { getAtan } from "../math/helper";
import { store } from "../stores";

/**
 * This draws the main game table in the browser.
 * The game table is implemented as a graphic 2D Canvas.
 */
class TableSingleton {
  private canvasContainer: HTMLElement;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private chatContainer: HTMLElement;

  private scale: Vector2;
  private offset: Vector2;

  private gameKind: string;

  public get scaleX() {
    return this.scale.x;
  }

  public get scaleY() {
    return this.scale.y;
  }

  public get offsetX() {
    return this.offset.x;
  }

  public get offsetY() {
    return this.offset.y;
  }

  public set gameType(gameKind: string) {
    this.gameKind = gameKind;
  }

  public init(): void {
    // initialise the table canvas
    const canvas: HTMLCanvasElement = document.getElementById(GameConfig.screenId) as HTMLCanvasElement;
    const container: HTMLElement = document.getElementById(GameConfig.gameAreaId) as HTMLElement;
    this.canvasContainer = container;
    this.canvas = canvas;
    //@ts-ignore
    this.context = this.canvas.getContext('2d');

    // initialise the chat
    const chatContainer: HTMLElement = document.getElementById(GameConfig.chatId) as HTMLElement;
    this.chatContainer = chatContainer;

    this.resizeCanvas();
  }

  /**
   * This resizes the canvas when its streached.
   * I think this can be replaced with CSS or SCSS.
   */
  public resizeCanvas(): void {
    const originalCanvasWidth = GameConfig.gameSize.x;
    const originalCanvasHeight = GameConfig.gameSize.y;
    const widthToHeight: number = originalCanvasWidth / originalCanvasHeight;

    let newHeight: number = window.innerHeight * 0.85;
    let newWidth: number = window.innerWidth * 0.85;

    const newWidthToHeight: number = newWidth / newHeight;

    if (newWidthToHeight > widthToHeight) {
      newWidth = newHeight * widthToHeight;
    } else {
      newHeight = newWidth / widthToHeight;
    }

    if (this.canvasContainer) {
      this.canvasContainer.style.width = newWidth + 'px';
      this.canvasContainer.style.height = newHeight + 'px';
      this.canvasContainer.style.marginTop = (window.innerHeight - newHeight) / 2 + 'px';
      this.canvasContainer.style.marginBottom = (window.innerHeight - newHeight) / 2 + 'px';
      this.scale = new Vector2(newWidth / originalCanvasWidth, newHeight / originalCanvasHeight);

      this.canvas.width = newWidth;
      this.canvas.height = newHeight;

      if (this.canvas.offsetParent) {
        this.offset = new Vector2(this.canvas.offsetLeft, this.canvas.offsetTop);
      }
    }

    if (this.chatContainer) {
      this.chatContainer.style.width = (newWidth * 0.75) + 'px';
    }
  }

  public clear(): void {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * This draws the hit line.
   * 
   * @param position - the position of the hit line
   * @param rotation - the rotation angle of the hit line
   */
  public drawHitLine(position: IVector2 = { x: 0, y: 0 }, rotation = 0): void {
    this.context.save();
    this.context.scale(this.scale.x, this.scale.y);
    this.context.translate(position.x, position.y);
    this.context.beginPath();
    this.context.moveTo(0, 0);
    this.context.rotate(rotation);
    this.context.setLineDash([10, 15]);
    this.context.lineWidth = 3;
    this.context.strokeStyle = 'orange';
    this.context.lineTo(1500, 0);
    this.context.stroke();
    this.context.restore();
  }

  /**
   * This draws the table image.
   * 
   * @param sprite - the sprite image element
   * @param position - the position of the table sprite image
   * @param rotation - the rotation of the table sprite image
   * @param origin - the original position of the sprite image 
   */
  public drawImage(sprite: HTMLImageElement, position: IVector2 = { x: 0, y: 0 },
    rotation = 0, origin: IVector2 = { x: 0, y: 0 }): void {
    this.context.save();
    this.context.scale(this.scale.x, this.scale.y);
    this.context.translate(position.x, position.y);
    this.context.rotate(rotation);
    this.context.drawImage(sprite, 0, 0, sprite.width, sprite.height,
      -origin.x, -origin.y, sprite.width, sprite.height);
    this.context.restore();
  }

  /**
   * Draw the ball on the table.
   * 
   * @param sprite - the sprite image
   * @param position - the position of the ball sprite
   * @param rotation - the rotation angle of the ball sprite
   * @param labelOrient - the orientation of the ball label
   * @param ballNumber - the ball number
   * @param shadow - if the ball has a shadow this is "true" or "false" if not
   */
  public drawBall(sprite: HTMLImageElement, position: IVector2 = { x: 0, y: 0 },
    rotation: Orientation, labelOrient: Orientation, ballNumber: number,
    shadow = false): void {
    
    let spriteWidth = 48;
    let spriteHeight = 48;
    let spriteX;
    let spriteY;
    let phi;

    if (ballNumber > 8) {
      spriteWidth = 50;
      spriteHeight = 50;
      phi = -rotation.phi - Math.PI / 2;
      const theta = rotation.theta < 0 ? Math.PI * 2 + rotation.theta : rotation.theta;
      const spriteStep = Math.PI / 41;
      let spriteSection = Math.round(theta / spriteStep) + 1;
      if (spriteSection === 42) {
        spriteSection = 1;
      }
      spriteX = spriteWidth * ((spriteSection - 1) % 5);
      spriteY = spriteHeight * (Math.ceil(spriteSection / 5) - 1);
    } else {
      spriteX = spriteWidth * (ballNumber % 3);
      spriteY = spriteHeight * Math.floor(ballNumber / 3);
    }

    this.context.save();
    this.context.scale(this.scale.x, this.scale.y);
    this.context.translate(position.x, position.y);
    
    if (ballNumber > 8) {
      this.context.rotate(phi);
    }

    // drawing shadow
    if (shadow) {
      this.context.beginPath();
      this.context.shadowColor = 'black';
      this.context.shadowOffsetX = ((position.x - GameConfig.gameSize.x / 2) / GameConfig.gameSize.x) * 10;
      this.context.shadowOffsetY = ((position.y - GameConfig.gameSize.y / 2) / GameConfig.gameSize.y) * 10;
      this.context.shadowBlur = 8;
      this.context.arc(0, 0, GameConfig.ball.diameter / 2, 0, Math.PI * 2, true); // outer circle
      this.context.fill();
    }

    // drawing ball
    this.context.drawImage(sprite, spriteX, spriteY, spriteWidth, spriteHeight,
      -GameConfig.ball.diameter / 2, -GameConfig.ball.diameter / 2,
      GameConfig.ball.diameter, GameConfig.ball.diameter);
    
    this.context.restore();

    // drawing spot on the ball
    if (this.gameKind === 'POOL8'|| this.gameKind === 'POOL9') {
      this.drawSpot(position, rotation, labelOrient, ballNumber);
    } else if (this.gameKind === 'PRACTICE' &&
      (store.state.settings.defaultPracticeGame === 'POOL8' ||
        store.state.settings.defaultPracticeGame === 'POOL9')) {    
      this.drawSpot(position, rotation, labelOrient, ballNumber);
    } 

    // adding light and shadow gradient on the ball
    this.context.save();
    this.context.scale(this.scale.x, this.scale.y);
    this.context.translate(position.x, position.y);
    this.context.beginPath();
    this.context.translate(-10, -10);

    // defining gradient for light and shadow
    const gradient = this.context.createRadialGradient(2, 2, 3, 10, 10, GameConfig.ball.diameter / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,0.5)');
    gradient.addColorStop(0.7, 'rgba(0,0,0,0.3)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.8)');

    this.context.fillStyle = gradient;
    this.context.arc(10, 10, GameConfig.ball.diameter / 2 + 1, 0, Math.PI * 2, true);
    this.context.fill();
    this.context.restore();
  }

  /**
   * Draw the spot on the ball.
   * 
   * @param position - the position of the spot on the table
   * @param rotation - the ball rotation
   * @param labelPosition - the position of the spot on the ball
   * @param ballNumber - the ball number
   */
  public drawSpot(position: IVector2 = { x: 0, y: 0 },
    rotation: Orientation, labelPosition: Orientation, ballNumber: number): void {
    const baseSpotRadius = 10;
    const spriteWidth = 38;
    const spriteHeight = 38;

    if (labelPosition.z > 0.08) {
      // get spots sprite
      const sprite = Assets.getSprite(GameConfig.sprites.paths.labelsSprite);

      // chose correct sprite from the file according to ballnum
      const spriteX = spriteWidth * (ballNumber % 4);
      const spriteY = spriteHeight * Math.floor(ballNumber / 4);

      // position, coordinates of spot center
      const { x, y, z } = labelPosition;

      // angle of spot center from ball center
      const baseAlpha = getAtan(x, y);

      // angle of spot rotation based on ball orientation in 3D space
      const newOrient = Orientation.copy(rotation);
      newOrient.rotateZ(-labelPosition.phi);
      newOrient.rotateY(-labelPosition.theta);
      const addAlpha = newOrient.phi + Math.PI;

      // Rotating the sprite of ball spot before drawing.
      const canvas = document.createElement('canvas');
      canvas.setAttribute('width', spriteWidth.toString());
      canvas.setAttribute('height', spriteHeight.toString());
      const ctx = canvas.getContext('2d');
      //@ts-ignore
      ctx.translate(spriteWidth / 2, spriteHeight / 2);
      //@ts-ignore
      ctx.rotate(-addAlpha);
      //@ts-ignore
      ctx.drawImage(sprite, spriteX, spriteY, spriteWidth, spriteHeight,
        -spriteWidth / 2, -spriteHeight / 2, spriteWidth, spriteHeight);

      // spot shrinking scale for being close to the edge of ball
      const scaleY = z * 0.8 + 0.2;

      // Drawing the spot.
      this.context.save();
      this.context.scale(this.scale.x, this.scale.y);
      this.context.translate(
        position.x + ((x * GameConfig.ball.diameter) / 2) * 0.8,
        position.y - ((y * GameConfig.ball.diameter) / 2) * 0.8
      );
      this.context.rotate(-baseAlpha + Math.PI / 2);
      this.context.drawImage(canvas, -baseSpotRadius, -baseSpotRadius * scaleY,
        baseSpotRadius * 2, baseSpotRadius * 2 * scaleY
      );

      this.context.restore();
    }
  }

  /**
   * This is used for drawing the legacy menu text.
   */
  public drawText(text: string, font: string, color: string,
    position: IVector2, textAlign = 'left'): void {
    this.context.save();
    this.context.scale(this.scale.x, this.scale.y);
    this.context.fillStyle = color;
    this.context.font = font;
    this.context.textAlign = textAlign as CanvasTextAlign;
    this.context.fillText(text, position.x, position.y);
    this.context.restore();
  }

  /**
   * This is used for the legacy menu.
   * 
   * @param cursor - the cursor style
   */
  public changeCursor(cursor: string): void {
    this.canvas.style.cursor = cursor;
  }
}

export const table = new TableSingleton();
window.addEventListener('resize', table.resizeCanvas.bind(table));
